import {
  AddAgentRequest,
  EappButtonPressedRequest,
  EappButtonPressedResponse,
  ApiUsageResponse,
  DeleteAgentRequest,
  DeleteAgentResponse,
  HiddenCompanies,
  HideCompanyRequest,
  HideCompanyResponse,
  Member,
  PublicInfoResponse,
  SignupRequest,
  SignupResponse,
  UpdateDefaultStateRequest,
  UpdateDefaultStateResponse,
  UpdatePasswordRequest,
  UpdatePasswordResponse,
  UpdateTelesalesRequest,
  UpdateTellesalesResponse,
  UpdateUserInfoRequest,
  UpdateUserInfoResponse,
} from '../models/member.model';
import { catchError, Observable, of } from 'rxjs';
import { HttpClientInterface } from '@itk/http-client-interface';
import { ErrorResponse, HttpErrorResponse } from '../models/error.model';

export class MemberEndpoint {
  constructor(private readonly http: HttpClientInterface) {}

  logEappButtonPressed(
    body: EappButtonPressedRequest,
  ): Observable<EappButtonPressedResponse> {
    return this.http.post<EappButtonPressedResponse, EappButtonPressedRequest>({
      url: '/member/eapp-button-pressed/',
      body,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  getPublicInfo(
    liteToken: string,
  ): Observable<PublicInfoResponse | ErrorResponse> {
    return this.http
      .get<PublicInfoResponse>({
        url: '/member/public-info/',
        queryParams: { token: liteToken },
        headers: {},
      })
      .pipe(
        catchError((e: unknown) => {
          return of((e as HttpErrorResponse).error);
        }),
      );
  }

  getMemberWithTokenSpecified(
    token: string,
  ): Observable<Member | ErrorResponse> {
    return this.http
      .get<Member>({
        url: '/member/',
        headers: {
          Authorization: `token ${token}`,
        },
      })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  getMember(): Observable<Member | ErrorResponse> {
    return this.http
      .get<Member>({ url: '/member/' })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  getMemberAgents(): Observable<Member[] | ErrorResponse> {
    return this.http
      .get<Member[]>({ url: '/member/agency/' })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  signup(
    memberSignupInfo: SignupRequest,
  ): Observable<SignupResponse | ErrorResponse> {
    return this.http
      .post<SignupResponse, SignupRequest>({
        url: '/member/signup/',
        body: memberSignupInfo,
      })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  getHiddenCompanies(
    toolkit: string,
  ): Observable<HiddenCompanies | ErrorResponse> {
    return this.http
      .get<HiddenCompanies>({
        url: '/member/hidden/',
        queryParams: { toolkit: toolkit },
      })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  toggleHiddenCompany(
    req: HideCompanyRequest,
  ): Observable<HideCompanyResponse | ErrorResponse> {
    return this.http
      .post<HideCompanyResponse, HideCompanyRequest>({
        url: '/member/hidden/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  updateMemberUserInfo(
    memberId: number,
    req: UpdateUserInfoRequest,
  ): Observable<UpdateUserInfoResponse | ErrorResponse> {
    return this.http
      .patch<UpdateUserInfoResponse, UpdateUserInfoRequest>({
        url: `/member/${memberId}/`,
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  updateMemberUserPassword(
    memberId: number,
    req: UpdatePasswordRequest,
  ): Observable<UpdatePasswordResponse | ErrorResponse> {
    return this.http
      .patch<UpdatePasswordResponse, UpdatePasswordRequest>({
        url: `/member/${memberId}/`,
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  updateMemberIsTellesales(
    memberId: number,
    req: UpdateTelesalesRequest,
  ): Observable<UpdateTellesalesResponse | ErrorResponse> {
    return this.http
      .patch<UpdateTellesalesResponse, UpdateTelesalesRequest>({
        url: `/member/${memberId}/`,
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  updateDefaultState(
    memberId: number,
    req: UpdateDefaultStateRequest,
  ): Observable<UpdateDefaultStateResponse | ErrorResponse> {
    return this.http
      .patch<UpdateDefaultStateResponse, UpdateDefaultStateRequest>({
        url: `/member/${memberId}/`,
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  addAgent(req: AddAgentRequest): Observable<Member | ErrorResponse> {
    return this.http
      .post<Member, AddAgentRequest>({
        url: '/member/agency/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  deleteAgent(
    req: DeleteAgentRequest,
  ): Observable<DeleteAgentResponse | ErrorResponse> {
    return this.http
      .delete<DeleteAgentResponse, DeleteAgentRequest>({
        url: '/member/agency/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError((e: unknown) => of((e as HttpErrorResponse).error)));
  }

  getApiUsage(): Observable<ApiUsageResponse | null> {
    return this.http
      .get<ApiUsageResponse>({ url: '/member/api-usage/' })
      .pipe(catchError(() => of(null)));
  }
}
